import Button from 'ext-ui/components/Button/Button'
import React, { useCallback, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import PayBillFlyout from 'src/component-flyout/PayBillFlyout/PayBillFlyout'
import PaymentDetailFlyout from 'src/component-flyout/PaymentDetailFlyout/PaymentDetailFlyout'
import LayoutPage from 'src/component-layout/LayoutPage/LayoutPage'
import Box from 'src/components/Box/Box'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import NotFoundBackgroundPage from 'src/components/NotFoundBackgroundPage/NotFoundBackgroundPage'
import { useCurrentUser } from 'src/context/UserProvider/useUser'
import { useTranslate } from 'src/localization/Trns'
import { UserFeature } from 'src/models/user.models'
import { useFlyoutRoute } from 'src/navigation/modalParams'
import { bankAccountsUrl, vendorsUrl } from 'src/navigation/urls'
import { useQueryUserFeatures } from 'src/queries/auth.queries'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import { selectUserExplicitOrganizationIdFromState } from 'src/redux/selectors/data/organizations.selectors'
import { selectLoadedFromRemoteConfigState } from 'src/redux/selectors/data/remoteConfig.selectors'
import { selectAppName } from 'src/redux/selectors/session.selectors'
import { canManageBillPay } from 'src/utils/billPayAccess'
import { isBillPayEnabled } from 'src/utils/featureFlags'

import PaymentHistoryTable from './PaymentHistoryTable'
import type { PaymentHistoryUrlState } from './paymentHistoryUrlState'
import {
  parsePaymentHistoryUrlState,
  serializePaymentHistoryUrlState,
} from './paymentHistoryUrlState'
import style from './PaymentsPage.style'

/**
 * Bill Pay M3 — the Payments page (EX-40607 / EX-40568).
 * Gating is character-for-character the Vendors page's, via the same shared
 * helpers, so the three Bill Pay surfaces can never disagree about access.
 */
const PaymentsPage: React.FC = () => {
  const translate = useTranslate()
  const history = useHistory()
  const location = useLocation()
  const paymentFlyout = useFlyoutRoute()
  const currentUser = useCurrentUser()
  const appName = useSelector(selectAppName)
  const remoteConfig = useSelector(selectFeatureFlaggerFromState)
  const loadedRemoteConfig = useSelector(selectLoadedFromRemoteConfigState)
  const organizationId = useSelector(selectUserExplicitOrganizationIdFromState)

  // The page owns the Pay Bill flyout's open/close state; the flyout is a
  // controlled child (same ownership split as VendorsPage ↔ AddVendorFlyout).
  const [isPayBillOpen, setIsPayBillOpen] = useState(false)
  const paymentHistoryUrlState = useMemo(
    () => parsePaymentHistoryUrlState(location.search),
    [location.search],
  )
  const setPaymentHistoryUrlState = useCallback(
    (state: PaymentHistoryUrlState): void => {
      history.replace({
        pathname: location.pathname,
        search: serializePaymentHistoryUrlState(history.location.search, state),
      })
    },
    [history, location.pathname],
  )

  const billPayPaymentsEnabled = isBillPayEnabled(remoteConfig, organizationId)

  // Gated on the flag so a user without Bill Pay access never fires a dark
  // features request (same convention as VendorsPage / BankAccountsPage,
  // EX-40413 review fix).
  const { data: userFeatures, isLoading: userFeaturesLoading } =
    useQueryUserFeatures(
      [UserFeature.VIEW_PAYABLES, UserFeature.ORGANIZATION_USER_STATISTICS],
      {
        enabled: billPayPaymentsEnabled,
      },
    )

  const canViewPayables = userFeatures?.[UserFeature.VIEW_PAYABLES] ?? false
  // Owner/admin + full-visibility bookkeepers (EX-40473). Shared helper so this
  // gate and the nav item can never diverge — see src/utils/billPayAccess.
  const hasAccess =
    billPayPaymentsEnabled &&
    canManageBillPay({
      hasOrgAdminPermissions: currentUser?.hasOrgAdminPermissions,
      organizationRole: currentUser?.organizationRole,
      orgUserStatisticsEnabled:
        userFeatures?.[UserFeature.ORGANIZATION_USER_STATISTICS],
    }) &&
    canViewPayables

  if (
    !loadedRemoteConfig ||
    currentUser === undefined ||
    (billPayPaymentsEnabled && userFeaturesLoading)
  ) {
    return <LoadingBackgroundPage />
  }

  if (!hasAccess) {
    return <NotFoundBackgroundPage />
  }

  return (
    <LayoutPage fixedHeight>
      <Helmet>
        <title>{`${translate('Payments')} | ${appName}`}</title>
      </Helmet>
      <div className={'PaymentsPage'} css={style} data-testid={'payments-page'}>
        <div className={'payments-header'}>
          <h1 className={'payments-title'}>{translate('Payments')}</h1>
          {/* Primary CTA in the header, matching the Vendors page. The flyout
              inherits the page's flag + tier + role gating by being mounted
              inside it — no new gate. */}
          <Button
            variant={'primary'}
            onClick={() => setIsPayBillOpen(true)}
            data-testid={'pay-bill-button'}
          >
            {translate('Pay Bill')}
          </Button>
        </div>
        <Box noPadding noShadow outlineBorder className={'payments-box'}>
          {organizationId && (
            <PaymentHistoryTable
              organizationId={organizationId}
              viewerUserId={currentUser.id}
              urlState={paymentHistoryUrlState}
              onUrlStateChange={setPaymentHistoryUrlState}
              onOpenPayment={(paymentId) =>
                paymentFlyout.open('payment', { paymentId })
              }
              onAddBankAccount={() =>
                history.push(bankAccountsUrl(organizationId))
              }
              onAddVendor={() => history.push(vendorsUrl())}
            />
          )}
        </Box>
        {/* organizationId is always resolved once `hasAccess` is true (the
            Bill Pay gate is derived from it), so this renders in practice;
            the guard just satisfies the flyout's `string` org-id contract. */}
        {organizationId && (
          <PaymentDetailFlyout
            organizationId={organizationId}
            viewerUserId={currentUser.id}
            paymentId={
              paymentFlyout.name === 'payment'
                ? paymentFlyout.params.paymentId
                : undefined
            }
            onClose={paymentFlyout.close}
          />
        )}
        {organizationId && (
          <PayBillFlyout
            isOpen={isPayBillOpen}
            onClose={() => setIsPayBillOpen(false)}
            organizationId={organizationId}
            viewerUserId={currentUser.id}
          />
        )}
      </div>
    </LayoutPage>
  )
}

export default PaymentsPage
